import { container } from '@game-studio/arcade-base';
import { Container, Graphics } from 'pixi.js';
import { SymbolView } from './symbol-view';
import { GameState, GoldState, gameModel } from '../../models/game-model';
import { CollectorState } from '../../services/collector-service';
import { symbolService } from '../../services/symbol-service';

export enum PanelColor {
    LIGHTBLUE = 0,
    BLUE = 1,
    DARKBLUE = 2,
    PURPLE = 3,
    YELLOW = 4,
    ORANGE = 5,
    RED = 6
}

export class GameBoardView extends Container {
    private _boundBox: Graphics;
    private _symbolsContainer: Container;
    private _symbols: SymbolView[];

    constructor() {
        super();
        this.buildSymbols();
        this.buildSizer();
        this.addListeners();
    }

    // Hide elements before drawing.
    public preDraw(): void {
        this._symbolsContainer.visible = false;
    }

    // Show elements after drawing.
    public postDraw(): void {
        this._symbolsContainer.visible = true;
    }

    private buildSizer(): void {
        this._boundBox = new Graphics();
        this.addChild(this._boundBox);
        this._boundBox.beginFill(0xffffff, 0);
        this._boundBox.drawRect(0, 0, this._symbolsContainer.width, this._symbolsContainer.height);
    }

    private buildSymbols(): void {
        const rows: number = 3;
        const columns: number = 4;
        const horizontalSpacing: number = 200;
        const verticalSpacing: number = 150;

        this._symbols = [];
        this._symbolsContainer = new Container();
        this.addChild(this._symbolsContainer);

        // Index and position symbols
        let $symbolID = 0;
        for (let $row = 0; $row < rows; $row++) {
            for (let $column = 0; $column < columns; $column++) {
                const symbol = new SymbolView($symbolID++);
                this._symbols.push(symbol);
                this._symbolsContainer.addChild(symbol);
                symbol.x = horizontalSpacing * $column;
                symbol.y = verticalSpacing * $row;
            }
        }
    }

    private onAutoPlay$(): void {
        // Check autoplay and game in play.
        if (!gameModel.autoPlay || gameModel.gameState !== GameState.IN_PLAY) {
            return;
        }
        // Check for avalible symbol to call.
        const avalibleSymbolIndex: number = symbolService.avalibleSymbol();
        if (avalibleSymbolIndex === -1) {
            return;
        }
        // Call to reveal symbol.
        const symbol: SymbolView = this._symbols[avalibleSymbolIndex];
        symbol.reveal();

        // Begin next autoplay call.
        const baseDelay: number = 500;
        const staggerDelay: number = 50;
        const revealTime: number = baseDelay + symbolService.countedSymbols() * staggerDelay;
        setTimeout(() => {
            this.onAutoPlay$();
        }, revealTime);
    }

    private onGameUpdate$(): void {
        // Check for endgame state.
        const endGameCallDelay: number = 1500;
        if (gameModel.collectedStates.indexOf(CollectorState.Available) === CollectorState.Error) {
            gameModel.gameState = GameState.COMPLETE;
            setTimeout(() => {
                container.cabinet.endGame();
            }, endGameCallDelay);
        }
    }

    private onGoldState$(): void {
        // Continue autoplay after instant prize.
        if (gameModel.goldState === GoldState.COMPLETE) {
            gameModel.invalidate(() => gameModel.autoPlay);
        }
    }

    private addListeners(): void {
        gameModel.bindToValue(
            this,
            () => gameModel.collectorStates,
            () => this.onGameUpdate$
        );

        gameModel.bindToValue(
            this,
            () => gameModel.autoPlay,
            () => this.onAutoPlay$
        );

        gameModel.bindToValue(
            this,
            () => gameModel.goldState,
            () => this.onGoldState$
        );
    }
}
